import { GraduationCap, Languages } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";

interface EducationLanguageCardProps {
  profile: any;
}

export function EducationLanguageCard({ profile }: EducationLanguageCardProps) {
  const { t } = useTranslation(["talent", "common"]);

  const getGermanLevelLabel = (level: string) => {
    if (!level) return null;
    return t(
      `forms.sections.language_skills.levels.${level.toLowerCase()}`,
      level.toUpperCase()
    );
  };

  const getGermanLevelBadgeColor = (level: string) => {
    switch (level.toLowerCase()) {
      case "a1":
      case "a2":
        return "bg-red-100 text-red-800 border-red-200";
      case "b1":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "b2":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "c1":
        return "bg-green-100 text-green-800 border-green-200";
      case "c2":
        return "bg-emerald-100 text-emerald-800 border-emerald-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getLanguageSchoolLabel = (school: string) => {
    if (!school) return null;
    return t(`forms.sections.language_skills.schools.${school}`, school);
  };

  return (
    <div className="bg-white border rounded-lg p-4 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <GraduationCap className="h-5 w-5 text-gray-600" />
        <h3 className="font-semibold text-gray-900">Language & Education</h3>
      </div>
      <div className="space-y-4">
        {profile.german_language_skills && (
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Languages className="h-5 w-5 text-blue-600 flex-shrink-0" />
                <span className="font-medium text-gray-900">German Level</span>
              </div>
              <Badge
                variant="outline"
                className={`px-3 py-1 text-sm font-semibold ${getGermanLevelBadgeColor(profile.german_language_skills)}`}
              >
                {profile.german_language_skills.toUpperCase()}
              </Badge>
            </div>
            <div className="mt-2">
              <span className="text-sm text-gray-600">
                {getGermanLevelLabel(profile.german_language_skills)}
              </span>
            </div>
          </div>
        )}

        {profile.language_school && (
          <div className="flex items-center gap-2">
            <GraduationCap className="h-4 w-4 text-gray-600 flex-shrink-0" />
            <span className="text-sm text-gray-600">
              Language School: {getLanguageSchoolLabel(profile.language_school)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
